// apps/web/src/components/painel/home/assinatura-cobertura-po.tsx — Fase 17.8, Plano 19 (D-12; UI-SPEC U-21, U-24).
// Assinatura "Cobertura do pó por cor · dias de consumo restantes" de Compras: até 6 cores (o botão "Ver todas" expande no próprio painel
// até o que a API devolveu), cada uma com a amostra da cor RAL (48px), um trilho de 4px com os dias de cobertura (transform: scaleX,
// proporcional ao maior; o traço marca o limite de dias da resposta), o código e os dias restantes ("6 d"). Abaixo do limite: trilho e
// dias em --color-warning, ícone de alerta e o texto oculto "abaixo do limite de N dias" (a cor nunca é o único sinal). Cor sem consumo
// medido na janela mostra "—" e "sem consumo medido": sem medição, nunca "0 d". O limite vem da resposta, nunca de um literal.
import { useId, useState, type ReactElement } from 'react';
import { AlertTriangle, Boxes } from 'lucide-react';
import type { CoberturaPoCor } from '@pieltec/contracts';
import { formatarNumero } from '../../../lib/painel/home/formato';
import { amostraDaCor, CELULAS_VISIVEIS_PO, posicaoDoMinimo } from './assinatura-po-estoque';
import { EstadoVazio } from './estado-vazio';
import '../../../styles/painel-home.css';

export const TEXTO_SEM_PO_COBERTURA = 'Nenhum pó com saldo cadastrado.';

export interface AssinaturaCoberturaPoProps {
  readonly cores: readonly CoberturaPoCor[];
  /** Todas as cores de pó com saldo ou com consumo (pode ser maior que `cores.length`). */
  readonly totalCores: number;
  /** Dias de cobertura abaixo dos quais a cor é alerta (vem da resposta). */
  readonly limiteDias: number;
}

export interface AssinaturaCoberturaPoVisaoProps extends AssinaturaCoberturaPoProps {
  readonly expandido: boolean;
  readonly onAlternar: () => void;
}

/** Fração do trilho preenchida (0 a 1, três casas): dias sobre a maior referência da lista; referência inválida não preenche. */
export function nivelDosDias(dias: number, referenciaDias: number): number {
  if (!Number.isFinite(dias) || !Number.isFinite(referenciaDias) || referenciaDias <= 0 || dias <= 0) return 0;
  return Math.min(1, Math.round((dias / referenciaDias) * 1000) / 1000);
}

/** "6 d", "9,5 d", "1.234 d": uma casa só quando os dias são fracionados. */
export function rotuloDeDias(dias: number): string {
  return `${formatarNumero(dias, Number.isInteger(dias) ? 0 : 1)} d`;
}

const kg = (valor: number): string => `${formatarNumero(valor, Number.isInteger(valor) ? 0 : 1)} kg`;

function Celula({ cor, referenciaDias, limiteDias }: { readonly cor: CoberturaPoCor; readonly referenciaDias: number; readonly limiteDias: number }): ReactElement {
  const amostra = amostraDaCor(cor.corCodigo);
  const medido = cor.dias.estado === 'MEDIDO';
  const dias = cor.dias.estado === 'MEDIDO' ? cor.dias.valor : null;
  const posicao = medido ? posicaoDoMinimo(limiteDias, referenciaDias) : null;
  return (
    <li className="painel-po" data-testid="painel-cobertura-cor" data-abaixo={cor.abaixoDoLimite ? 'true' : 'false'}>
      <span className={amostra.classe} style={amostra.hex === undefined ? undefined : { background: amostra.hex }} aria-hidden="true" />
      <span className="painel-po__trilho" aria-hidden="true">
        <span className={cor.abaixoDoLimite ? 'painel-po__nivel painel-po__nivel--abaixo' : 'painel-po__nivel'} style={{ transform: `scaleX(${dias === null ? 0 : nivelDosDias(dias, referenciaDias)})` }} />
        {posicao !== null && <span className="painel-po__minimo" style={{ left: `${posicao}%` }} />}
      </span>
      <span className="painel-po__codigo">{amostra.codigo}</span>
      <span className="painel-po__saldo">
        <span className="sr-only">{`${amostra.nome}: `}</span>
        {dias === null ? (
          <>
            <span>—</span>
            <span className="painel-po__sem-consumo">sem consumo medido</span>
          </>
        ) : (
          rotuloDeDias(dias)
        )}
        {cor.abaixoDoLimite && <AlertTriangle className="painel-po__alerta size-3" aria-hidden="true" />}
        {cor.abaixoDoLimite && <span className="sr-only">{`abaixo do limite de ${formatarNumero(limiteDias, 0)} ${limiteDias === 1 ? 'dia' : 'dias'}`}</span>}
        <span className="sr-only">{`, saldo ${kg(cor.saldoKg)}`}</span>
      </span>
    </li>
  );
}

/** Visão controlada (testável por SSR): `AssinaturaCoberturaPo` só guarda expandido/recolhido. */
export function AssinaturaCoberturaPoVisao({ cores, totalCores, limiteDias, expandido, onAlternar }: AssinaturaCoberturaPoVisaoProps): ReactElement {
  const idTitulo = useId();
  const idLista = useId();
  const visiveis = expandido ? cores : cores.slice(0, CELULAS_VISIVEIS_PO);
  // A referência do trilho é o maior entre os dias medidos de TODAS as cores recebidas e o limite, para que expandir não mude o nível das
  // primeiras e o traço do limite caiba sempre dentro do trilho.
  const referenciaDias = Math.max(limiteDias, ...cores.map((cor) => (cor.dias.estado === 'MEDIDO' ? cor.dias.valor : 0)));
  const temMais = totalCores > CELULAS_VISIVEIS_PO || cores.length > CELULAS_VISIVEIS_PO;
  return (
    <section className="painel-assinatura" data-testid="painel-assinatura" data-tipo="cobertura-po" aria-labelledby={idTitulo}>
      <header className="painel-assinatura__cabecalho">
        <h3 id={idTitulo} className="painel-assinatura__titulo">
          Cobertura do pó por cor
        </h3>
        <span className="painel-assinatura__subtitulo">dias de consumo restantes</span>
      </header>
      {cores.length === 0 ? (
        <EstadoVazio icone={Boxes} texto={TEXTO_SEM_PO_COBERTURA} />
      ) : (
        <>
          <ul id={idLista} className="painel-assinatura__grade painel-po__grade">
            {visiveis.map((cor) => (
              <Celula key={cor.corCodigo} cor={cor} referenciaDias={referenciaDias} limiteDias={limiteDias} />
            ))}
          </ul>
          {temMais && (
            <button type="button" className="painel-em-poder__ver-todos" aria-expanded={expandido} aria-controls={idLista} onClick={onAlternar} data-testid="painel-cobertura-ver-todas">
              {expandido ? 'Mostrar menos' : `Ver todas (${formatarNumero(Math.max(totalCores, cores.length))})`}
            </button>
          )}
          {expandido && totalCores > cores.length && (
            <p className="painel-em-poder__nota">{`Mostrando as ${formatarNumero(cores.length)} de menor cobertura de ${formatarNumero(totalCores)}.`}</p>
          )}
        </>
      )}
    </section>
  );
}

export function AssinaturaCoberturaPo(props: AssinaturaCoberturaPoProps): ReactElement {
  const [expandido, setExpandido] = useState(false);
  return <AssinaturaCoberturaPoVisao {...props} expandido={expandido} onAlternar={() => setExpandido((atual) => !atual)} />;
}
